import { useEffect, useState, useCallback } from "react";
import { getSessions, getSessionCustomerChats } from "../../Services/chatbot";
import "../../assets/chatbotconfig.css";
import "./sessionChatHistory.css";
import { toast } from "react-toastify";
import { buildTimeline } from "../../utils/sessionTurnUtils";
import RoomSearchCard from "./RoomSearchCard";
import BookingSummaryCard from "./BookingSummaryCard";
import BookingConfirmedCard from "./BookingConfirmedCard";
import ReviewImproveFaqsModal from "../ReviewImproveFaqsModal/index";
import { Bot, ChevronDown, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, CircleX, Clock, Lightbulb, Loader2, Mail,
  MessagesSquare, Phone, RefreshCw, Tag, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader, SectionCard } from "@/components/admin/cards";
import { cn } from "@/lib/utils";

const STATUS_CHIP = {
  answered: { label: "Answered", className: "bg-success-500/15 text-success-700", icon: <CircleCheck className="size-3" aria-hidden="true" /> },
  partial: { label: "Partial", className: "bg-warning-500/15 text-warning-700", icon: <CircleAlert className="size-3" aria-hidden="true" /> },
  failed: { label: "Failed", className: "bg-destructive/10 text-destructive", icon: <CircleX className="size-3" aria-hidden="true" /> },
};

// A guest message shown as a right-aligned bubble
const GuestBubble = ({ children }) => (
  <div className="flex justify-end">
    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground shadow-xs">
      {children}
    </div>
  </div>
);

const BotRow = ({ children }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
      <Bot className="size-4" />
    </span>
    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">{children}</div>
  </div>
);

const initials = (name) =>
  !name ? <User className="size-4" aria-hidden="true" /> :
    name.split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase();

// Short label from the session id so chats without guest details can be told apart
const chatCode = (sessionId) => (sessionId ? `#${String(sessionId).slice(0, 4).toUpperCase()}` : "");

const toDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

const clockTime = (dateStr) =>
  toDate(dateStr)?.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) || "";

const dayLabel = (dateStr) => {
  const date = toDate(dateStr);
  if (!date) return "Earlier";
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(new Date()) - startOf(date)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "short",
    ...(date.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
  });
};

// Consecutive sessions on the same day share one heading; API order is kept
const groupByDay = (sessions) =>
  sessions.reduce((groups, session) => {
    const label = dayLabel(session.started_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.sessions.push(session);
    else groups.push({ label, sessions: [session] });
    return groups;
  }, []);

const Chip = ({ icon, children }: any) => (
  <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium [&_svg]:size-3",
    "bg-primary/10 text-primary")}>
    {icon}{children}
  </span>
);

// Compact clickable row used for sessions and customers
const PersonRow = ({ name, avatarName, details, expanded, onClick, chip, small = false }: any) => (
  <button
    type="button"
    aria-expanded={expanded}
    onClick={onClick}
    className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-start hover:bg-accent/60"
  >
    <span className={cn("flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary",
      small ? "size-8 text-xs" : "size-10 text-sm")} aria-hidden="true">
      {initials(avatarName ?? name)}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-2">
        <span className="truncate font-semibold capitalize">{name}</span>
        {chip}
      </span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground [&_svg]:size-3.5">
        {details}
      </span>
    </span>
    <ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", expanded && "rotate-180")} aria-hidden="true" />
  </button>
);

const Meta = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1">{icon}{children}</span>
);


const SessionChatHistory = ({ setIsNeedPublish, setPublishTrigger }: any) => {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<any>([]);
  const [all_last_key, setAll_last_key] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [paginationKeys, setPaginationKeys] = useState<any>([]);
  const [hasInitialRecords, setHasInitialRecords] = useState(false);
  const [expandedSession, setExpandedSession] = useState<any>(null);
  const [expandedCustomer, setExpandedCustomer] = useState<any>(null);
  const [customerChats, setCustomerChats] = useState<any>({});
  const [loadingChats, setLoadingChats] = useState<any>({});
  const [expandedItems, setExpandedItems] = useState<any>({});
  const [limit] = useState(10);
  const [visibleTurnCounts, setVisibleTurnCounts] = useState<any>({});
  const TURNS_PER_PAGE = 10;

  // Review & Improve FAQs state
  const [showReviewFaqsModal, setShowReviewFaqsModal] = useState(false);
  const [addedFaqQuestions, setAddedFaqQuestions] = useState<any>([]);

  // Format relative time (same pattern as old chat history)
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    const diffMs = +now - +date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  };

  // Get customer display name
  const getCustomerName = (customer) => {
    if (!customer) return "";
    const first = customer.first_name || "";
    const last = customer.last_name || "";
    return `${first} ${last}`.trim();
  };

  // Mask email (same as old chat history)
  const maskEmail = (email) => {
    if (!email || email === "No email provided") return "";
    const parts = email.split("@");
    if (parts.length === 2) {
      return `${parts[0]}@*****`;
    }
    return email;
  };

  // Truncate text with "see more" (same as old chat history)
  const truncateText = (text, length = 100) => {
    if (!text) return "";
    if (text.length > length) {
      return (
        <>
          {text.slice(0, length)}
          <span className="see-more-link ms-1 font-medium underline-offset-2 opacity-80 hover:underline">...see more</span>
        </>
      );
    }
    return text;
  };

  const toggleExpand = (key, field) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: !prev[key]?.[field],
      },
    }));
  };

  // Load sessions (matches qna-stage loadHistory pattern)
  const loadSessions = useCallback(async (last_key = "", options: any = {}) => {
    const { preserveOnEmpty = false } = options;
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const res = await getSessions(limit, last_key || null);
      const records = res.data?.records || [];
      const nextKey = res.data?.last_evaluated_key || null;

      if (records.length > 0 || !preserveOnEmpty) {
        setSessions(records);
      }
      setAll_last_key(nextKey);
      setHasMore(!!nextKey);

      return { hasRecords: records.length > 0, nextKey };
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load sessions");
      return { hasRecords: false, nextKey: null };
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const handleNextPage = async () => {
    if (!hasMore || !all_last_key || loading) return;

    const currentKey = all_last_key;

    let { hasRecords, nextKey } = await loadSessions(currentKey, { preserveOnEmpty: true });

    if (!hasRecords && nextKey) {
      const retryResult = await loadSessions(nextKey, { preserveOnEmpty: true });
      hasRecords = retryResult.hasRecords;
      nextKey = retryResult.nextKey;

      if (!hasRecords) {
        setHasMore(false);
      }
    } else if (!hasRecords && !nextKey) {
      setHasMore(false);
    }

    if (hasRecords) {
      setPaginationKeys((prev) => [...prev, currentKey]);
      setPage((prev) => {
        const newPage = prev + 1;
        if (newPage === 2) {
          setHasInitialRecords(true);
        }
        return newPage;
      });
      setExpandedSession(null);
      setExpandedCustomer(null);
    }
  };

  const handlePreviousPage = async () => {
    if (page === 1) return;

    const previousKey = page === 2 ? null : paginationKeys[paginationKeys.length - 2];

    setPaginationKeys((prev) => prev.slice(0, -1));

    await loadSessions(previousKey || "");
    setPage((prev) => prev - 1);
    setExpandedSession(null);
    setExpandedCustomer(null);
  };

  useEffect(() => {
    setSessions([]);
    setAll_last_key(null);
    setPage(1);
    setHasMore(true);
    setPaginationKeys([]);
    setHasInitialRecords(false);

    const loadInitialData = async () => {
      const { hasRecords, nextKey } = await loadSessions("");

      if (!hasRecords && nextKey) {
        const retryResult = await loadSessions(nextKey);

        if (!retryResult.hasRecords) {
          setHasInitialRecords(false);
          setHasMore(false);
        } else {
          setHasInitialRecords(true);
        }
      } else if (hasRecords) {
        setHasInitialRecords(true);
      } else {
        setHasInitialRecords(false);
        setHasMore(false);
      }
    };

    loadInitialData();
  }, [loadSessions]);

  // Toggle session expand (only one at a time, like old chat history)
  // Chats with 0 or 1 person open their messages straight away
  const handleSessionClick = (session) => {
    const sessionId = session.session_id;
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setExpandedCustomer(null);
      return;
    }
    setExpandedSession(sessionId);
    setExpandedCustomer(null);
    const customers = session.customers || [];
    if (customers.length < 2) handleCustomerClick(sessionId, customers[0]?.customer_id || null);
  };

  // Load chats for sessions with no customer (only session_id)
  const loadSessionOnlyChats = async (sessionId) => {
    const key = `${sessionId}_no_customer`;
    setLoadingChats((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await getSessionCustomerChats(sessionId);
      setCustomerChats((prev) => ({
        ...prev,
        [key]: {
          customer: null,
          turns: res.data?.turns || [],
        },
      }));
    } catch (error) {
      console.error("Failed to load session chats:", error);
      toast.error("Failed to load conversation");
    } finally {
      setLoadingChats((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Toggle customer expand and load chats (also handles guest/no-customer)
  const handleCustomerClick = async (sessionId, customerId) => {
    const key = customerId ? `${sessionId}_${customerId}` : `${sessionId}_no_customer`;

    if (expandedCustomer === key) {
      setExpandedCustomer(null);
      return;
    }

    setExpandedCustomer(key);

    // Skip if already loaded
    if (customerChats[key]) return;

    // Load chats using only session_id when no customer_id
    if (!customerId) {
      loadSessionOnlyChats(sessionId);
      return;
    }

    setLoadingChats((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await getSessionCustomerChats(sessionId, customerId);
      setCustomerChats((prev) => ({
        ...prev,
        [key]: {
          customer: res.data?.customer || null,
          turns: res.data?.turns || [],
        },
      }));
    } catch (error) {
      console.error("Failed to load customer chats:", error);
      toast.error("Failed to load conversation");
    } finally {
      setLoadingChats((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Show more turns for a customer conversation
  const handleShowMore = (chatKey, e) => {
    e.stopPropagation();
    setVisibleTurnCounts((prev) => ({
      ...prev,
      [chatKey]: (prev[chatKey] || TURNS_PER_PAGE) + TURNS_PER_PAGE,
    }));
  };

  // Render a single timeline item (chat pair or booking card)
  const renderTimelineItem = (item, idx, chatKey) => {
    const pairKey = `${chatKey}_${idx}`;

    if (["rooms_shown", "offer_selected", "booking_summary", "booking_confirmed"].includes(item.itemType)) {
      const d = item.displayData || {};
      const priceDisplay = d.totalPrice !== undefined && d.totalPrice !== null
        ? `${d.currency || ""} ${Number(d.totalPrice).toLocaleString()}`
        : null;
      return (
        <div key={idx} className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
          {item.itemType !== "offer_selected" && item.userContext && <GuestBubble>{item.userContext}</GuestBubble>}
          <BotRow>
            {item.itemType === "rooms_shown" && (
              <RoomSearchCard rooms={item.displayData.rooms} searchDates={item.displayData.searchDates} />
            )}
            {item.itemType === "offer_selected" && (
              <div className="w-full max-w-xl rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <Tag className="size-4 text-warning-700" aria-hidden="true" />
                  <span className="font-semibold">Selected</span>
                  {d.roomName && (
                    <span>
                      {d.roomName}
                      {d.packageName && ` \u2022 ${d.packageName}`}
                    </span>
                  )}
                  {priceDisplay && (
                    <span className="ms-auto rounded-full bg-warning-500/15 px-2 py-0.5 text-xs font-medium text-warning-700">
                      {priceDisplay.trim()}
                    </span>
                  )}
                </div>
                {item.assistantReply && <p className="mt-1 text-muted-foreground">{item.assistantReply}</p>}
              </div>
            )}
            {item.itemType === "booking_summary" && <BookingSummaryCard content={item.displayData.content} />}
            {item.itemType === "booking_confirmed" && <BookingConfirmedCard displayData={item.displayData} />}
          </BotRow>
        </div>
      );
    }

    // Regular chat Q&A pair
    const queryStatus = item.hotel_query_analytics?.query_status;
    const status = queryStatus ? STATUS_CHIP[queryStatus] || STATUS_CHIP.failed : null;
    return (
      <div key={idx} className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        {item.question && (
          <GuestBubble>
            <p
              className="whitespace-pre-wrap break-words"
              onClick={(e) => {
                e.stopPropagation();
                if (item.question.length > 100) toggleExpand(pairKey, "question");
              }}
            >
              {expandedItems[pairKey]?.question ? item.question : truncateText(item.question, 100)}
            </p>
          </GuestBubble>
        )}
        <BotRow>
          {item.answer ? (
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-muted px-3 py-2 text-sm text-foreground">
              <p
                className="whitespace-pre-wrap break-words"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.answer.length > 100) toggleExpand(pairKey, "answer");
                }}
              >
                {expandedItems[pairKey]?.answer ? item.answer : truncateText(item.answer, 100)}
              </p>
            </div>
          ) : !item.question ? (
            <p className="text-sm italic text-muted-foreground">No content</p>
          ) : null}
          {(status || item.timestamp) && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {status && (
                <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium", status.className)}>
                  {status.icon}
                  {status.label}
                </span>
              )}
              {item.timestamp && <span>{new Date(item.timestamp).toLocaleString()}</span>}
            </div>
          )}
        </BotRow>
      </div>
    );
  };

  // Conversation panel for one customer / guest
  const renderConversation = (key, chatData, isLoadingChat) => {
    if (isLoadingChat) {
      return (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading conversation...
        </div>
      );
    }
    if (!chatData) return null;
    if (chatData.turns.length === 0) {
      return <p className="py-3 text-center text-sm text-muted-foreground">No messages found</p>;
    }
    const timeline = buildTimeline(chatData.turns);
    const visibleCount = visibleTurnCounts[key] || TURNS_PER_PAGE;
    const visible = timeline.slice(0, visibleCount);
    const hasMoreTurns = timeline.length > visibleCount;
    return (
      <div className="flex flex-col gap-4 rounded-xl border border-border bg-background p-4">
        {visible.map((item, idx) => renderTimelineItem(item, idx, key))}
        {hasMoreTurns && (
          <Button type="button" variant="outline" size="sm" className="self-center" onClick={(e) => handleShowMore(key, e)}>
            Show more ({timeline.length - visibleCount} remaining)
          </Button>
        )}
      </div>
    );
  };

  const renderContacts = (customers) => {
    const chips = customers.flatMap((customer, index) => {
      const name = getCustomerName(customer);
      const email = maskEmail(customer.email);
      return [
        name && <Chip key={`n${index}`} icon={<User aria-hidden="true" />}>{name}</Chip>,
        email && <Chip key={`e${index}`} icon={<Mail aria-hidden="true" />}>{email}</Chip>,
        customer.phone && <Chip key={`p${index}`} icon={<Phone aria-hidden="true" />}>{customer.phone}</Chip>,
      ].filter(Boolean);
    });
    if (customers.length > 1) {
      chips.push(<Chip key="count" icon={<Users aria-hidden="true" />}>{customers.length} guests</Chip>);
    }
    return chips.length > 0 ? <span className="flex flex-wrap items-center gap-1.5">{chips}</span> : null;
  };

  const renderPeople = (session, customers) => (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {customers.length} people in this chat · each person has their own messages
      </p>
      {customers.map((customer, custIndex) => {
        const key = `${session.session_id}_${customer.customer_id}`;
        const isCustomerExpanded = expandedCustomer === key;
        const name = getCustomerName(customer);
        const email = maskEmail(customer.email);
        return (
          <div key={customer.customer_id || custIndex} className="flex flex-col gap-2 rounded-lg border border-border bg-card p-1">
            <PersonRow
              small
              name={name || email || customer.phone || `Person ${custIndex + 1}`}
              avatarName={name}
              expanded={isCustomerExpanded}
              onClick={() => handleCustomerClick(session.session_id, customer.customer_id)}
              details={
                <>
                  {name && email && <Meta icon={<Mail aria-hidden="true" />}>{email}</Meta>}
                  {(name || email) && customer.phone && <Meta icon={<Phone aria-hidden="true" />}>{customer.phone}</Meta>}
                  {customer.updated_at && <Meta icon={<Clock aria-hidden="true" />}>{formatTimeAgo(customer.updated_at)}</Meta>}
                </>
              }
            />
            {isCustomerExpanded && renderConversation(key, customerChats[key], loadingChats[key])}
          </div>
        );
      })}
    </>
  );

  const renderSession = (session, sessionIndex) => {
    const isExpanded = expandedSession === session.session_id;
    const customers = session.customers || [];
    const directKey = customers[0]?.customer_id
      ? `${session.session_id}_${customers[0].customer_id}`
      : `${session.session_id}_no_customer`;
    const time = clockTime(session.started_at);
    return (
      <li key={session.session_id || sessionIndex}
        className={cn("rounded-xl border border-border bg-card transition-shadow", isExpanded && "shadow-sm ring-1 ring-primary/20")}>
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => handleSessionClick(session)}
          className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-start hover:bg-accent/60"
        >
          <span className="flex min-w-16 shrink-0 flex-col items-center rounded-lg bg-primary/10 px-2 py-1.5 leading-tight text-primary">
            {time ? <span className="text-sm font-bold">{time}</span> : <Clock className="size-4" aria-hidden="true" />}
            {session.started_at && <span className="text-[11px] font-medium opacity-80">{formatTimeAgo(session.started_at)}</span>}
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-semibold">Chat {chatCode(session.session_id)}</span>
            {renderContacts(customers)}
          </span>
          <ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", isExpanded && "rotate-180")} aria-hidden="true" />
        </button>
        {isExpanded && (
          <div className="flex flex-col gap-2 border-t border-border bg-muted/40 p-3">
            {customers.length < 2
              ? renderConversation(directKey, customerChats[directKey], loadingChats[directKey])
              : renderPeople(session, customers)}
          </div>
        )}
      </li>
    );
  };

  const handleRefresh = async () => {
    setPage(1);
    setPaginationKeys([]);
    setAll_last_key(null);
    setHasMore(true);
    setHasInitialRecords(false);
    setSessions([]);
    setExpandedSession(null);
    setExpandedCustomer(null);
    setCustomerChats({});
    setExpandedItems({});
    const { hasRecords, nextKey } = await loadSessions("");
    if (!hasRecords && nextKey) {
      const retryResult = await loadSessions(nextKey);
      if (!retryResult.hasRecords) {
        setHasInitialRecords(false);
        setHasMore(false);
      } else {
        setHasInitialRecords(true);
      }
    } else if (hasRecords) {
      setHasInitialRecords(true);
    } else {
      setHasInitialRecords(false);
      setHasMore(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loaderOverlay zIndex-1023">
          <div className="loading">
            <div className="main-loader"></div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4">
        <PageHeader id="chatHistoryHeading" icon={<MessagesSquare />} title="Chat History"
          description="See what guests asked your chatbot and how it answered." />
        <div data-evo-island>
          <SectionCard
            icon={<MessagesSquare />}
            title="Conversations"
            description={sessions.length > 0
              ? `${sessions.length} chat${sessions.length !== 1 ? "s" : ""} on this page, grouped by day. Click a chat to read it.`
              : "Guest chats appear here, newest first."}
            actions={
              <>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowReviewFaqsModal(true)}>
                  <Lightbulb aria-hidden="true" />
                  Review &amp; Improve FAQs
                </Button>
                <Button type="button" size="sm" onClick={handleRefresh}>
                  <RefreshCw aria-hidden="true" />
                  Refresh
                </Button>
              </>
            }
          >
            {sessions.length === 0 && !loading ? (
              <EmptyState icon={<MessagesSquare />} title="No conversations yet"
                description="When guests chat with your assistant, their conversations will show up here." />
            ) : (
              <div className="flex flex-col gap-4" id="sessionTabContent">
                {groupByDay(sessions).map((group, groupIndex) => (
                  <section key={`${group.label}-${groupIndex}`} aria-label={group.label} className="flex flex-col gap-2">
                    <h3 className="px-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
                    <ul className="flex flex-col gap-2">
                      {group.sessions.map((session, sessionIndex) => renderSession(session, sessionIndex))}
                    </ul>
                  </section>
                ))}
              </div>
            )}

            {hasInitialRecords || page > 1 ? (
              <nav aria-label="pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-sm">
                <span className="text-muted-foreground">
                  Page <span className="font-semibold text-foreground">{page}</span>
                  {sessions.length > 0
                    ? ` (showing ${sessions.length} ${sessions.length === 1 ? "session" : "sessions"})`
                    : " (no sessions found)"}
                  {!hasMore && " - end of results"}
                </span>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="icon" className="size-8" aria-label="Previous page"
                    onClick={handlePreviousPage} disabled={page === 1 || loading}>
                    <ChevronLeft aria-hidden="true" />
                  </Button>
                  <span className="flex size-8 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground" aria-current="page">
                    {page}
                  </span>
                  <Button type="button" variant="outline" size="icon" className="size-8" aria-label="Next page"
                    onClick={handleNextPage} disabled={!hasMore || loading}>
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </div>
              </nav>
            ) : null}
          </SectionCard>
        </div>
      </div>

      {/* Review & Improve FAQs Modal */}
      <ReviewImproveFaqsModal
        show={showReviewFaqsModal}
        onHide={() => setShowReviewFaqsModal(false)}
        onFaqAdded={(question) => setAddedFaqQuestions(prev => [...prev, question])}
        addedFaqQuestions={addedFaqQuestions}
        setIsNeedPublish={setIsNeedPublish}
        setPublishTrigger={setPublishTrigger}
      />
    </>
  );
};

export default SessionChatHistory;
